/**
 * US-051 G3 (AC-G3a) — typed resource-scoped vocabulary helpers for the
 * custom-role permission editor.
 *
 * The catalog (`@livingsyncs/types`) is the single source of truth:
 *  - `RESOURCES` — 50 resources
 *  - `ACTION_KEYS` — 10 elevated action gates (e.g. `medication:administer`)
 *  - `RESOURCE_SCOPED_KEYS` — the authoritative 104 grantable keys
 *
 * These helpers turn that flat vocabulary into a per-resource shape the UI can
 * render (Read / Write / actions), while guaranteeing we never offer a key that
 * is not actually in `RESOURCE_SCOPED_KEYS` (e.g. read-only resources have no
 * Write toggle; `user-role` is action-only).
 */
import {
  RESOURCES,
  ACTION_KEYS,
  RESOURCE_SCOPED_KEYS,
  keyFor,
  type Resource,
  type ActionKey,
} from "@livingsyncs/types";

/** Fast membership test against the authoritative 104. */
const RESOURCE_SCOPED_KEY_SET: ReadonlySet<string> = new Set(
  RESOURCE_SCOPED_KEYS,
);

/** True when `key` is one of the 104 new-vocabulary grantable keys. */
export function isResourceScopedKey(key: string): boolean {
  return RESOURCE_SCOPED_KEY_SET.has(key);
}

/** Fast membership test for the 10 elevated action gates. */
const ACTION_KEY_SET: ReadonlySet<string> = new Set(ACTION_KEYS);

/**
 * True when `key` is an elevated action gate (e.g. `case:close`,
 * `medication:administer`) rather than a `:read`/`:write` verb. Action gates are
 * deliberately **NOT implied by `<resource>:write`** (the §3.2 non-implication
 * rule) and are granted on their own — the UI badges them so they aren't mistaken
 * for ordinary resource permissions.
 */
export function isActionKey(key: string): boolean {
  return ACTION_KEY_SET.has(key);
}

/**
 * The resource a key belongs to — the segment before the FIRST `:`.
 * Matched exactly (not by prefix) so `appointment:force-complete` resolves to
 * `appointment`, never to the distinct resource `appointment-form-rule`.
 */
export function resourceOfKey(key: string): Resource | null {
  const idx = key.indexOf(":");
  if (idx === -1) return null;
  const candidate = key.slice(0, idx);
  return (RESOURCES as readonly string[]).includes(candidate)
    ? (candidate as Resource)
    : null;
}

/** Action keys (e.g. `:administer`, `:close`) that belong to a resource. */
export function actionKeysFor(resource: Resource): readonly ActionKey[] {
  return ACTION_KEYS.filter(
    (k) => resourceOfKey(k) === resource && isResourceScopedKey(k),
  );
}

/** The verb segment of an action key — the part after the resource's `:`. */
export function actionVerbOf(actionKey: ActionKey): string {
  const idx = actionKey.indexOf(":");
  return idx === -1 ? actionKey : actionKey.slice(idx + 1);
}

export interface ResourceRow {
  resource: Resource;
  /** Human-readable label, e.g. `care-plan` → "Care plan". */
  label: string;
  /** `<resource>:read` if it exists in the catalog, else null. */
  readKey: string | null;
  /** `<resource>:write` if it exists in the catalog, else null. */
  writeKey: string | null;
  /** Elevated action keys for this resource (may be empty). */
  actionKeys: readonly ActionKey[];
}

/**
 * Title-cases a kebab-case resource id for display.
 * `care-plan` → "Care plan", `audit-log` → "Audit log".
 */
export function humanizeResource(resource: string): string {
  const spaced = resource.replace(/-/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Humanize an action verb for a toggle label.
 * `force-complete` → "Force complete", `ssn-read` → "Ssn read".
 */
export function humanizeAction(verb: string): string {
  const spaced = verb.replace(/-/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Build the per-resource editor rows from the typed catalog.
 * Resources are returned alphabetically (matching `RESOURCES` order).
 * A resource with no grantable keys at all is omitted (defensive; none exist
 * today but keeps the UI honest if the catalog changes).
 */
export function buildResourceRows(): ResourceRow[] {
  return RESOURCES.map((resource): ResourceRow => {
    const readKey = keyFor(resource, "read");
    const writeKey = keyFor(resource, "write");
    return {
      resource,
      label: humanizeResource(resource),
      readKey: isResourceScopedKey(readKey) ? readKey : null,
      writeKey: isResourceScopedKey(writeKey) ? writeKey : null,
      actionKeys: actionKeysFor(resource),
    };
  }).filter(
    (row) =>
      row.readKey !== null ||
      row.writeKey !== null ||
      row.actionKeys.length > 0,
  );
}
